// app-subdomain-creators/src/router/index.js

import { createRouter, createWebHistory } from 'vue-router';
// import { createFeatureStore } from '../stores/featureStore';

// Static imports for views
import CreatorLogin from '../views/CreatorLogin.vue';
import CreatorPasswordRecover from '../views/CreatorPasswordRecover.vue';
import CreatorPasswordReset from '../views/CreatorPasswordReset.vue';
import StaffInviteAccept from '../views/StaffInviteAccept.vue';
import CreatorActivationHandoff from '../views/CreatorActivationHandoff.vue';
import LayoutSwitcher from '../layouts/LayoutSwitcher.vue';

// Static imports for pages
import CreatorDashboard from '../accountPages/CreatorDashboard.vue';
import CreatorUsers from '../accountPages/CreatorUsers.vue';
import CreatorSupport from '../accountPages/CreatorSupport.vue';
import CreatorAnalytics from '../accountPages/CreatorAnalytics.vue';
import CartPageMain from '../accountPages/CartPageMain.vue';
import CartProductsTab from '../accountPages/CartProductsTab.vue';
import PaymentsPageCreatorMain from '../accountPages/PaymentsPageCreatorMain.vue';
import PaymentsPageCreatorOverviewTab from '../accountPages/PaymentsPageCreatorOverviewTab.vue';
import PaymentsPageCreatorPaymentsReceivedTab from '../accountPages/PaymentsPageCreatorPaymentsReceivedTab.vue';
import PaymentsPageCreatorUpcomingRenewalsTab from '../accountPages/PaymentsPageCreatorUpcomingRenewalsTab.vue';
import PaymentsPageCreatorPayoutsTab from '../accountPages/PaymentsPageCreatorPayoutsTabV2.vue';
import PaymentsPageCreatorBankAccountsTab from '../accountPages/PaymentsPageCreatorBankAccountsTab.vue';
import AdvertisingPageCreator from '../accountPages/AdvertisingPageCreator.vue';
import CreatorEngagement from '../accountPages/CreatorEngagement.vue';
import NotificationsPageCreator from '../accountPages/NotificationsPageCreator.vue';
import ChatPageCreator from '../accountPages/ChatPageCreator.vue';
import StreamPageCreator from '../accountPages/StreamPageCreator.vue';
import MeetPageCreator from '../accountPages/MeetPageCreator.vue';
import NewsfeedPageCreator from '../accountPages/NewsfeedPageCreator.vue';
import BroadcastPageCreator from '../accountPages/BroadcastPageCreator.vue';
import CreatorThemeEditor from '../accountPages/CreatorThemeEditor.vue';
import CreatorPageEditor from '../accountPages/CreatorPageEditor.vue';
import CreatorAppSettings from '../accountPages/CreatorAppSettings.vue';
import CreatorProfileSettings from '../accountPages/CreatorProfileSettings.vue';
import AccountsPageCreator from '../accountPages/AccountsPageCreator.vue';
import AccountsPageGeneralCreator from '../accountPages/AccountsPageGeneralCreator.vue';
import AccountsPageBillingCreator from '../accountPages/AccountsPageBillingCreator.vue';
import AccountsPageUserPermissions from '../accountPages/AccountsPageUserPermissions.vue';
import AccountsPageUserSessionsCreator from '../accountPages/AccountsPageUserSessionsCreator.vue';
import AccountsPageSecurityCreator from '../accountPages/AccountsPageSecurityCreator.vue';
import CreatorEditorFeatureSelect from '../accountPages/CreatorEditorFeatureSelect.vue';
// import CreatorEditorChatProduct from '../accountPages/ChatPage.vue';
// import CreatorEditorCourseModuleProduct from '../accountPages/CourseModulePage.vue';
// import CreatorEditorVideoChannelProduct from '../accountPages/VideoChannelPage.vue';
// import CreatorEditorVideoChannelWatchPageProduct from '../accountPages/VideoChannelWatchPage.vue';
// import CreatorEditorVideoChannelContentManageProduct from '../accountPages/VideoChannelEditorContentManagePage.vue';
// import VideoChannelStudioEditVideoPage from '../accountPages/VideoChannelStudioEditVideoPage.vue';
// import CreatorEditorWebinarProduct from '../accountPages/WebinarPage.vue';
// import CreatorEditorWebinarWatchPageProduct from '../accountPages/WebinarLiveWatchPage.vue';
// import CreatorEditorShopifyStorefrontProduct from '../accountPages/ShopifyStoreFrontPage.vue';
// import CreatorEditorShopifyStoreProductPage from '../accountPages/ShopifyStoreProductPage.vue';
// import CreatorEditorNewsfeedMainPage from '../accountPages/NewsfeedPage.vue';
import CreatorEditorSettingsPage from '../accountPages/SettingsPage.vue';
import CreatorEditorBillingPage from '../accountPages/BillingPage.vue';
import CreatorMembersList from '../accountPages/CreatorMemberList.vue';
import CreatorLeadsList from '../accountPages/CreatorLeadsList.vue';
import CreatorMemberTags from '../accountPages/CreatorMemberTags.vue';
import CreatorMemberDetails from '../accountPages/CreatorMemberDetails.vue';
import CreatorLeadDetails from '../accountPages/CreatorLeadDetails.vue';
import {
  fetchCreatorStaffSession,
  canAccessCreatorPath,
  getFirstAccessibleCreatorPath,
} from '../utils/creatorStaffAccess';
// New Layouts (lazy-loaded via LayoutSwitcher)
import PageThemeEditorLayout from '../layouts/PageThemeEditorLayout.vue';
import SitePageEditorLayout from '../layouts/SitePageEditorLayout.vue';
import CreatorMediaContentMainDrive from '../accountPages/CreatorMediaContentMainDrive.vue';

const apiUrl = process.env.VUE_APP_PLATFORMA_API_BACKEND_URL;
const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL || '';
const authApiBaseUrl = apiProxyUrl
  ? `${String(apiProxyUrl).replace(/\/+$/, '')}/api`
  : apiUrl;

// Route guard for authentication
const requireAuth = async (to, from, next) => {
  try {
    const response = await fetch(`${authApiBaseUrl}/auth/validate-session`, {
      method: 'GET',
      credentials: 'include',
    });

    if (response.ok) {
      next();
    } else {
      next({ name: 'Login', query: { continue: to.fullPath } });
    }
  } catch (error) {
    console.error('Error validating session:', error);
    next({ name: 'Login', query: { continue: to.fullPath } });
  }
};

// Define static routes
const routes = [
  ...(process.env.NODE_ENV === 'development'
    ? [{
      path: '/preview',
      component: LayoutSwitcher,
      meta: { layout: 'creator' },
      children: [{
        path: 'advertising',
        name: 'CreatorAdvertisingPreview',
        component: AdvertisingPageCreator,
        meta: { layout: 'creator' },
      }],
    }]
    : []),
  {
    path: '/',
    name: 'Login',
    component: CreatorLogin,
  },
  {
    path: '/password-recover',
    name: 'CreatorPasswordRecover',
    component: CreatorPasswordRecover,
  },
  {
    path: '/password-reset',
    name: 'CreatorPasswordReset',
    component: CreatorPasswordReset,
  },
  {
    path: '/staff/accept',
    name: 'StaffInviteAccept',
    component: StaffInviteAccept,
  },
  {
    path: '/activation-handoff',
    name: 'CreatorActivationHandoff',
    component: CreatorActivationHandoff,
  },
  {
    path: '/creator',
    component: LayoutSwitcher,
    beforeEnter: requireAuth,
    meta: { layout: 'creator' },
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: CreatorDashboard,
        meta: { layout: 'creator' },
      },
      {
        path: 'users',
        name: 'Users',
        component: CreatorUsers,
        redirect: { name: 'MembersList' },
        meta: { layout: 'creator' },
        children: [
          {
            path: 'leads',
            name: 'LeadsList',
            component: CreatorLeadsList,
          },
          {
            path: 'members-list',
            name: 'MembersList',
            component: CreatorMembersList,
          },
          {
            path: 'members-tags',
            name: 'MemberTags',
            component: CreatorMemberTags,
          },
          {
            path: 'members-details/:memberId',
            name: 'MemberDetails',
            component: CreatorMemberDetails,
            props: true,
          },
          {
            path: 'leads-details/:leadId',
            name: 'LeadDetails',
            component: CreatorLeadDetails,
            props: true,
          },
        ],
      },
      {
        path: 'support',
        name: 'Support',
        component: CreatorSupport,
        meta: { layout: 'creator' },
      },
      {
        path: 'content-drive',
        name: 'ContentDriveMain',
        component: CreatorMediaContentMainDrive,
        meta: { layout: 'creator' },
      },
      {
        path: 'analytics',
        name: 'Analytics',
        component: CreatorAnalytics,
        meta: { layout: 'creator' },
      },
      {
        path: 'cart',
        name: 'CreatorCart',
        component: CartPageMain,
        redirect: { name: 'CreatorCartProducts' },
        meta: { layout: 'creator' },
        children: [
          {
            path: 'products',
            name: 'CreatorCartProducts',
            component: CartProductsTab,
            meta: { layout: 'creator' },
          },
        ],
      },
      {
        path: 'payments',
        name: 'CreatorPayments',
        component: PaymentsPageCreatorMain,
        redirect: { name: 'CreatorPaymentsOverview' },
        meta: { layout: 'creator' },
        children: [
          {
            path: 'overview',
            name: 'CreatorPaymentsOverview',
            component: PaymentsPageCreatorOverviewTab,
            meta: { layout: 'creator' },
          },
          {
            path: 'payments-received',
            name: 'CreatorPaymentsReceived',
            component: PaymentsPageCreatorPaymentsReceivedTab,
            meta: { layout: 'creator' },
          },
          {
            path: 'upcoming-renewals',
            name: 'CreatorPaymentsUpcomingRenewals',
            component: PaymentsPageCreatorUpcomingRenewalsTab,
            meta: { layout: 'creator' },
          },
          {
            path: 'payouts',
            name: 'CreatorPayouts',
            component: PaymentsPageCreatorPayoutsTab,
            meta: { layout: 'creator' },
          },
          {
            path: 'bank-accounts',
            name: 'CreatorPaymentBankAccounts',
            component: PaymentsPageCreatorBankAccountsTab,
            meta: { layout: 'creator' },
          },
        ],
      },
      {
        path: 'advertising',
        name: 'CreatorAdvertising',
        component: AdvertisingPageCreator,
        meta: { layout: 'creator' },
      },
      {
        path: 'notifications',
        name: 'CreatorNotifications',
        component: NotificationsPageCreator,
        meta: { layout: 'creator' },
      },
      {
        path: 'notifications/:notificationSlug',
        name: 'CreatorNotificationSlug',
        component: NotificationsPageCreator,
        meta: { layout: 'creator' },
        props: true,
      },
      // Engage section (top-level)
      {
        path: 'chat',
        name: 'Chat',
        component: ChatPageCreator,
        meta: { layout: 'creator' },
      },
      {
        path: 'stream',
        name: 'Stream',
        component: StreamPageCreator,
        meta: { layout: 'creator' },
      },
      {
        path: 'meet',
        name: 'Meet',
        component: MeetPageCreator,
        meta: { layout: 'creator' },
      },
      {
        path: 'newsfeed',
        name: 'Newsfeed',
        component: NewsfeedPageCreator,
        meta: { layout: 'creator' },
      },
      {
        path: 'newsfeed/post/:postSlug',
        name: 'NewsfeedPostSlug',
        component: NewsfeedPageCreator,
        meta: { layout: 'creator' },
        props: true,
      },
      {
        path: 'broadcast',
        name: 'Broadcast',
        component: BroadcastPageCreator,
        meta: { layout: 'creator' },
      },
      {
        path: 'engagement',
        name: 'Engagement',
        component: CreatorEngagement,
        meta: { layout: 'creator' },
      },
      {
        path: 'page-editor',
        name: 'PageEditor',
        component: CreatorPageEditor,
        meta: { layout: 'creator', requiresCreatorPlatform: true }, // ✅ tell LayoutSwitcher to use CreatorLayout
      },
      {
        path: 'app-settings',
        name: 'AppSettings',
        component: CreatorAppSettings,
        redirect: { name: 'AppSettingsDomains' },
        meta: { layout: 'creator', requiresCreatorPlatform: true },
        children: [
          {
            path: 'domains',
            name: 'AppSettingsDomains',
            component: () => import('../accountPages/CreatorAppSettingsDomains.vue'),
          },
          {
            path: 'integrations',
            name: 'AppSettingsPaymentsMain',
            component: () => import('../accountPages/CreatorIntegrationsMain.vue'),
          },
          {
            path: 'payments',
            redirect: { name: 'AppSettingsPaymentsMain' },
          },
          {
            path: 'crypto',
            name: 'AppSettingsCryptoMain',
            component: () => import('../accountPages/CreatorAppSettingsCryptoMain.vue'),
          },
          {
            path: 'affiliates',
            name: 'AppSettingsAffiliates',
            component: () => import('../accountPages/CreatorAppSettingsAffiliates.vue'),
          },
          {
            path: 'plan-tiers',
            name: 'AppSettingsPlanTiersMain',
            component: () => import('../accountPages/CreatorAppSettingsPlanTiersMain.vue'),
          },
          {
            path: 'access-controls',
            name: 'AppSettingsAccessControlsMain',
            component: () => import('../accountPages/CreatorAppSettingsAccessControls.vue'),
          },
          {
            path: 'emails',
            name: 'AppSettingsEmailsMain',
            component: () => import('../accountPages/CreatorAppSettingsEmails.vue'),
          },
          {
            path: 'conversion-tracking',
            name: 'AppSettingsConversionTracking',
            component: () => import('../accountPages/CreatorConversionTracking.vue'),
          },
        ],
      },
      {
        path: 'profile-settings',
        name: 'ProfileSettings',
        component: CreatorProfileSettings,
      },
      {
        path: 'account',
        component: AccountsPageCreator,
        redirect: { name: 'CreatorAccountGeneral' },
        children: [
          {
            path: 'general',
            name: 'CreatorAccountGeneral',
            component: AccountsPageGeneralCreator,
          },
          {
            path: 'billing',
            name: 'CreatorAccountBilling',
            component: AccountsPageBillingCreator,
          },
          {
            path: 'user-permissions',
            name: 'CreatorAccountUserPermissions',
            component: AccountsPageUserPermissions,
          },
          {
            path: 'user-sessions',
            name: 'CreatorAccountUserSessions',
            component: AccountsPageUserSessionsCreator,
          },
          {
            path: 'security',
            name: 'CreatorAccountSecurity',
            component: AccountsPageSecurityCreator,
          },
        ],
      },
      {
        path: 'editor-feature-select',
        name: 'CreatorEditorFeatureSelect',
        component: CreatorEditorFeatureSelect,
      },
      {
        path: 'stockroom/:featureSlug/:tab?',
        name: 'CreatorStorefrontStockroomPage',
        component: () => import('../accountPages/StorefrontStockroomCreator.vue'),
        props: true,
        meta: { layout: 'creator' },
      },
      {
        path: 'stockroom/:featureSlug/orders/:orderId',
        name: 'CreatorStorefrontOrderDetailsPage',
        component: () => import('../accountPages/StorefrontOrderDetailsCreator.vue'),
        props: true,
        meta: { layout: 'creator' },
      },

      {
      path: 'page-theme-editor',
      name: 'PageThemeEditor',
      component: PageThemeEditorLayout,
      meta: { layout: 'page-theme-editor', requiresCreatorPlatform: true },
    },
    {
      path: 'site-page-editor/:pageId',
      name: 'SitePageEditor',
      component: SitePageEditorLayout,
      meta: { layout: 'site-page-editor', requiresCreatorPlatform: true },
      props: true,
    },
    {
      path: 'themes',
      name: 'CreatorThemeLibrary',
      component: () => import('../accountPages/CreatorThemeLibrary.vue'),
      meta: { layout: 'creator', requiresCreatorPlatform: true },
    },

/* -----------------------------
         PRIMARY THEME EDITOR ROUTE
         Dynamic slug route lives here
      ----------------------------- */
      {
        path: 'theme-editor',
        name: 'ThemeEditor',
        component: CreatorThemeEditor,
        meta: { layout: 'default', requiresCreatorPlatform: true },
        children: [
          {
            path: '',
            name: 'CreatorDashboardPreview',
            component: () => import('@/accountPages/DashboardCreatorEditor.vue'),
          },
          {
            path: 'member-theme-preview',
            name: 'CreatorMemberThemePreview',
            component: () => import('@/accountPages/MemberThemePreviewDashboard.vue'),
          },
          {
            path: 'editor-member-settings',
            name: 'CreatorEditorSettingsPage',
            component: CreatorEditorSettingsPage,
          },
          {
            path: 'editor-member-billing',
            name: 'CreatorEditorBillingPage',
            component: CreatorEditorBillingPage,
          },

          // ⛳ THIS IS WHERE FEATURE INSTANCE ROUTES LOAD
          {
            path: ':featureSlug/podcast/:episodeSlug',
            name: 'CreatorPodcastEpisodePage',
            component: () => import('@/Components/DynamicFeatureRenderer.vue'),
            props: true,
          },
          {
            path: ':featureSlug/articles/:articleSlug',
            name: 'CreatorArticlesArticlePage',
            component: () => import('@/Components/DynamicFeatureRenderer.vue'),
            props: true,
          },
          {
            path: ':featureSlug/watch/:videoSlug',
            name: 'CreatorEditorVideoChannelWatchPageProduct',
            component: () => import('@/accountPages/VideoChannelWatchPage.vue'),
            props: true,
          },
          {
            path: ':featureSlug/studio',
            name: 'CreatorEditorVideoChannelContentManageProduct',
            component: () => import('@/accountPages/VideoChannelEditorContentManagePage.vue'),
            props: true,
          },
          {
            path: ':featureSlug/studio/edit/:videoSlug',
            name: 'VideoChannelStudioEditVideoPage',
            component: () => import('@/accountPages/VideoChannelStudioEditVideoPage.vue'),
            props: true,
          },
          {
            path: ':featureSlug/flix-watch/:videoSlug',
            name: 'CreatorEditorFlixTheatreWatchPageProduct',
            component: () => import('@/accountPages/FlixTheatreWatchPageCreator.vue'),
            props: true,
          },
          {
            path: ':featureSlug/flix-studio',
            name: 'CreatorEditorFlixTheatreContentManageProduct',
            component: () => import('@/accountPages/FlixTheatreContentStudioCreator.vue'),
            props: true,
          },
          {
            path: ':featureSlug/flix-studio/edit/:videoSlug',
            name: 'FlixTheatreStudioEditVideoPage',
            component: () => import('@/accountPages/FlixTheatreStudioEditVideoPage.vue'),
            props: true,
          },
          {
            path: ':featureSlug/product/:productId?',
            name: 'CreatorEditorShopifyStoreProductPage',
            component: () => import('@/accountPages/ShopifyStoreProductPage.vue'),
            props: true,
          },
          {
            path: ':featureSlug/shopify-product/:productId?',
            name: 'CreatorEditorShopifyConnectedProductPage',
            component: () => import('@/accountPages/StorefrontShopifyConnectedProductPage.vue'),
            props: true,
          },
          {
            path: ':featureSlug/storefront-settings',
            name: 'CreatorEditorStorefrontSettingsPage',
            component: () => import('@/accountPages/StorefrontSettingsCreator.vue'),
            props: true,
          },
          {
            path: ':featureSlug/stockroom/orders/:orderId',
            name: 'CreatorEditorStorefrontOrderDetailsPage',
            component: () => import('@/accountPages/StorefrontOrderDetailsCreator.vue'),
            props: true,
          },
          {
            path: ':featureSlug/stockroom/:tab?',
            name: 'CreatorEditorStorefrontStockroomPage',
            component: () => import('@/accountPages/StorefrontStockroomCreator.vue'),
            props: true,
          },
          {
            path: ':featureSlug/shopify-stockroom/:tab?',
            name: 'CreatorEditorShopifyStockroomPage',
            component: () => import('@/accountPages/StorefrontShopifyStockroomCreator.vue'),
            props: true,
          },
          {
            path: ':featureSlug',
            name: 'DynamicFeatureRenderer',
            component: () => import('@/Components/DynamicFeatureRenderer.vue'),
            props: true,
          },
        ],
      },
    ],
  },
];

// Initialize router
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});
const confirmedMemberThemeCreators = new Set();

router.beforeEach(async (to, from, next) => {
  if (!String(to.path || '').startsWith('/creator')) {
    return next();
  }

  try {
    if (to.matched.some((record) => record.meta?.requiresCreatorPlatform)) {
      const platformResponse = await fetch(`${apiProxyUrl}/api/creator-platform/status`, {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
      });
      if (platformResponse.ok) {
        const platformPayload = await platformResponse.json().catch(() => ({}));
        if (!platformPayload?.platform?.isProvisioned) {
          return next({
            name: 'Dashboard',
            query: { provisionPlatform: '1', continue: to.fullPath },
          });
        }
      }
    }

    const staffSession = await fetchCreatorStaffSession();
    if (staffSession?.isStaff && !canAccessCreatorPath(staffSession, to.path)) {
      const fallbackPath = getFirstAccessibleCreatorPath(staffSession);
      if (String(to.path || '') !== fallbackPath) {
        return next({ path: fallbackPath });
      }
    }

    const enteringThemeEditor = String(to.path || '').startsWith('/creator/theme-editor')
      && !String(from.path || '').startsWith('/creator/theme-editor');
    const themeCreatorKey = String(
      staffSession?.creator?.userId
      || staffSession?.creator?.id
      || staffSession?.creator?.email
      || 'current-creator'
    ).trim().toLowerCase();
    if (enteringThemeEditor && !confirmedMemberThemeCreators.has(themeCreatorKey)) {
      try {
        const themeResponse = await fetch(`${apiProxyUrl}/api/app-settings/theme`, {
          method: 'GET',
          credentials: 'include',
          cache: 'no-store',
        });
        if (themeResponse.ok) {
          const themeSettings = await themeResponse.json().catch(() => ({}));
          if (!String(themeSettings?.theme?.key || '').trim()) {
            return next({
              name: 'CreatorThemeLibrary',
              query: { continue: to.fullPath },
            });
          }
          confirmedMemberThemeCreators.add(themeCreatorKey);
        }
      } catch (error) {
        console.warn('[theme-editor] Theme selection check failed; opening the editor.', error);
      }
    }

    return next();
  } catch (_error) {
    return next();
  }
});



export default router;







